<template>
    <div v-link-click-tracker
         :class="{ '--hide-prices': !showPrices, '--is-prerender': uaIsPrerender() }">
        <SkipLinks/>
        <div class="flex flex-col bg-gray-300">
            <div class="flex flex-col min-h-screen">
                <PageHeader/>
                <main class="main w-full min-h-screen"
                      :class="{'tunnel-view': isTunnelView, 'take-over': isTakeOverEnabled, 'no-mobile-brands': hideMobileBrands, 'search-page': isSearchPage }">
                    <div v-if="showBreadcrumb"
                         class="o-chrome-container pt-5 min-h-60">
                        <BreadCrumb/>
                    </div>
                    <router-view v-slot="{ Component }">
                        <transition appear
                                    mode="out-in"
                                    enter-active-class="animated fadeIn u-anim-dur-250"
                                    leave-active-class="animated fadeOut u-anim-dur-150">
                            <component :is="Component"/>
                        </transition>
                    </router-view>
                </main>
                <PageFooter class="mt-40"/>
            </div>
        </div>

        <ErrorToaster/>
        <ModalsContainer/>
        <SitePickerModule/>
        <AuthenticationModule/>
        <AppLoadModalShower/>
        <ProductCompareCurtain v-if="showProductCompareCurtain"/>
    </div>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageHeader from '@/project/layout/page-header/PageHeader.vue';
import PageFooter from '@/project/layout/page-footer/PageFooter.vue';
import BreadCrumb from '@/project/layout/breadcrumb/Breadcrumb.vue';
import SkipLinks from '@/core/accessibility/SkipLinks.vue';
import appStore from '@/core/app/app.store';
import ErrorToaster from '@/core/messages/ErrorToaster.vue';
import ModalsContainer from '@/project/modals/ModalsContainer.vue';
import SitePickerModule from '@/project/site/SitePickerModule.vue';
import AuthenticationModule from '@/project/auth/AuthenticationModule.vue';
import themeService from '@/project/app/themes/theme.service';
import authService from '@/core/auth/auth.service';
import cookieService from '@/core/cookie.service';
import siteService from '@/project/site/site.service';
import { PageName } from '@/router/routes/constants';
import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';
import AppLoadModalShower from '@/project/shared/AppLoadModalShower.vue';
import serverContext from '@/core/serverContext.service';
import { Feature } from '@/types/apiServerContract';
import { sessionStorageService } from '@/core/storage.service';
import urlHelperService from '@/core/urlHelper.service';
import { UrlQueryKey } from '@/core/urlQueryKey';
import { SITE_ORIGIN_LOCAL_STORAGE_KEY } from '@/project/config/constants';
import { uaIsPrerender } from '@/project/site/bot.utils';

const ProductCompareCurtain = defineAsyncComponent(() => import('@/project/product/compare/ProductCompareCurtain.vue'));

const route = useRoute();
const router = useRouter();

const pageTypeName = ref('');

const isTunnelView = computed((): boolean => {
    return appStore.isTunnelView;
});

const isTakeOverEnabled = computed(() => {
    return themeService.isTakeOverEnabledAndLoaded;
});

const hideMobileBrands = computed((): boolean => {
    // not logged in and on frontpage: true, else false
    if (isOnFrontpage.value && isLoggedIn.value === false) {
        return true;
    }
    return false;
});

const isLoggedIn = computed((): boolean => {
    return authService.isLoggedIn();
});

const isOnFrontpage = computed((): boolean => {
    return appStore.isFrontPage;
});

const isSearchPage = computed((): boolean => {
    return pageTypeName.value === PageName.SEARCH;
});

const showBreadcrumb = computed((): boolean => {
    return !appStore.hideBreadcrumb;
});

const showProductCompareCurtain = computed((): boolean => {
    return !isTunnelView.value && serverContext.isFeatureEnabled(Feature.ProductCompare);
});

const showPrices = computed((): boolean => {
    return myAccountStore.showPrices;
});

async function inititalizeAccountStore() {
    if (authService.isLoggedIn()) {
        await myAccountStore.init();
    }
}

function initOrigin() {
    const origin = urlHelperService.getQueryParam(UrlQueryKey.origin) || '';
    if (origin) {
        sessionStorageService.setItem(SITE_ORIGIN_LOCAL_STORAGE_KEY, origin);
    }
}

watch(() => route.path, () => {
    pageTypeName.value = route.name?.toString() || '';
}, { immediate: true });

onMounted(()=> {
    cookieService.set({ name: 'latestSiteVisited', value: siteService.getCulture() || undefined, exdays: 180, domain: location.hostname, path: '/' });
    inititalizeAccountStore();
    initOrigin();

    // Remove the old cookie-consent cookie if it exists
    try {
        cookieService.del('CookieInformationConsent');
    } catch (e) {
    }
});
router.beforeEach((to, from) => {
    appStore.previousRoute = from;
});
</script>

<style lang="less" scoped>
.main {
    display: flex;
    flex-direction: column;
    align-items: stretch;

    // IE11 sticky-footer trick. This and flex on outer.
    flex: 1 0 auto;
    margin-top: 10rem;

    transition: margin 500ms ease-in-out;
    &.no-mobile-brands {
        margin-top: 12rem;
    }
}

@screen md {
    .main {
        margin-top: 20rem;
        &.no-mobile-brands:not(.take-over) {
            margin-top: 14rem;
        }
        &.search-page:not(.take-over) {
            margin-top: 14rem;
        }

        &.take-over {
            margin-top: 0;
        }
    }
}

.main.tunnel-view {
    margin-top: 0;
}

</style>
